/**
 * Module-content insertion for the docx-editor.
 *
 * Unlike the slot-plugin rebuild (`./slots.ts`) — which assumed a
 * mostly-empty region between "Thank you for…" and "Yours sincerely…" —
 * this helper is surgical: it walks the loaded template, finds paragraphs
 * whose text matches a module name (case- and punctuation-insensitive),
 * and replaces the inline content of the paragraph immediately following
 * each match with the module's chosen text.
 *
 * Why surgical: the MEDirect templates ship with all their sections fully
 * drafted. The plan §3.1.4 idea of "rebuild this region from scratch"
 * would wipe the user's content. We just want to refresh chosen-choice
 * sections (Code of Conduct, Conflict of Interest, etc.) without
 * touching the rest.
 *
 * Matching strategy: normalise both sides (lowercase, strip leading
 * numbering like "1.", collapse whitespace, drop trailing punctuation)
 * and then compare with equality OR substring containment. Substring is
 * deliberate so "Background Information Provided" (module) matches
 * "BACKGROUND INFORMATION" (template heading) and vice versa.
 */
import { Fragment, type Mark, type Node as PMNode } from "prosemirror-model";
import type { EditorView } from "prosemirror-view";

import {
  buildInlineNodes as buildInlineNodesShared,
  firstRunMarks,
  visualParaAttrs,
} from "./para-clone";
import { type OutlineHints } from "./qta-insert";

/** Where to insert a module when its own heading is missing from the doc.
 *  `match` is matched against paragraph text via the same case- and
 *  punctuation-insensitive normaliser used for heading matching. */
export type AnchorPlacement = {
  position: "before" | "after";
  match: string;
};

export type ModuleInsert = {
  /** Module name as it appears in the template module list. */
  name: string;
  /** Plain-text content to render under the matching heading. */
  value: string;
  /** Heading paragraph to emit when inserting fresh (anchor fallback path).
   *  Defaults to `name`. Used so e.g. "CODE OF CONDUCT and Compliance"
   *  module can render under a shorter "CODE OF CONDUCT" heading. */
  headingLabel?: string;
  /** When the module's own heading is absent from the doc, try these
   *  anchor placements in order. First match wins; skipped when none match. */
  fallbackAnchors?: AnchorPlacement[];
  /** Insert (heading + empty paragraph) even when `value` is blank.
   *  Used so Code of Conduct shows its heading in the doc even when the
   *  case has no recorded jurisdiction yet. */
  alwaysInsert?: boolean;
  /** Treat `value` as a FALLBACK that must never overwrite the template's own
   *  content. When a matching heading already has non-blank body text, leave it
   *  exactly as authored; only fill an empty section or insert into a bare
   *  template. Used for the jurisdiction Code of Conduct default so a template
   *  that ships its own CoC declaration keeps it (QA: template CoC was being
   *  replaced by the generic jurisdiction text). */
  preserveExisting?: boolean;
};

const normalise = (s: string): string =>
  s
    .toLowerCase()
    .replace(/^\s*\d+[.)]\s*/, "") // strip leading "1." / "1)" numbering
    .replace(/[^a-z0-9\s]+/g, " ") // drop punctuation
    .replace(/\s+/g, " ")
    .trim();

const headingMatches = (paragraphText: string, moduleName: string): boolean => {
  const a = normalise(paragraphText);
  const b = normalise(moduleName);
  if (!a || !b) return false;
  if (a === b) return true;
  // Substring either way — handles "Background Information Provided"
  // (module) matching "Background Information" (template heading).
  return a.includes(b) || b.includes(a);
};

// MDRS-62: idempotency must check against `headingLabel` too. When a
// module's backend name doesn't substring-match the canonical inserted
// label (e.g. "Any Other Attendees" vs the inserted
// "INTERPRETER AND/OR OTHER ATTENDEES"), the previous fire's inserted
// heading goes unmatched and Path B re-inserts on every effect re-fire
// and every refresh, stacking duplicate headings in the doc.
const headingMatchTargets = (m: ModuleInsert): readonly string[] =>
  m.headingLabel && m.headingLabel !== m.name ? [m.name, m.headingLabel] : [m.name];

const anchorMatches = (paragraphText: string, match: string): boolean => {
  const a = normalise(paragraphText);
  const b = normalise(match);
  if (!a || !b) return false;
  return a.includes(b);
};

/** MDRS-97: gate Path A's heading matching so tab-aligned body rows
 *  (e.g. `"Unusual circumstances:\tThere were no unusual circumstances"`
 *  in VIC IME templates) don't masquerade as section headings, while
 *  still recognising actual heading paragraphs even when the backend's
 *  outline classifier missed them.
 *
 *  Resolution order:
 *    1. Backend hints (when present + matched): authoritative — if
 *       `template_outline._classify` bucketed the paragraph as
 *       `heading`, accept it. This is the strict signal.
 *    2. Local heuristic (always applied): reject paragraphs containing
 *       a tab character or longer than 100 chars. The original false-
 *       positive — tab-separated "label\tvalue" body rows — is caught
 *       here without depending on the outline being complete.
 *    3. Otherwise: accept.
 *
 *  Why fall back to the heuristic instead of being strict (originally
 *  was: "hints present and not in knownHeadings → reject"): some
 *  templates have headings styled in ways the backend classifier
 *  doesn't catch (case 951429 — `CODE OF CONDUCT` was a real heading
 *  in the template body but missing from `knownHeadings`). Treating
 *  hints as "must be there" produced the inverse bug: the exists-
 *  check failed to recognise an existing heading → Path B fired →
 *  duplicate preamble inserts on every render. The heuristic catches
 *  the original tab-row case without false-negativing on
 *  classifier-blind real headings. */
const isHeadingCandidate = (
  paragraphText: string,
  hints: OutlineHints | undefined
): boolean => {
  const text = paragraphText.trim();
  if (!text) return false;
  // Strict signal first: if hints say it's a heading, accept.
  if (hints?.knownHeadings.has(normalise(paragraphText))) return true;
  // Local heuristic — rules out the original tab-aligned body row
  // (`Unusual circumstances:\tValue`) without false-negativing on
  // unclassified real headings.
  if (text.includes("\t")) return false;
  if (text.length > 100) return false;
  // Reject paragraphs that look like sentences (terminal .?!). Body
  // lines like "I confirm I read the Conflict of Interest disclosure."
  // would otherwise substring-match a module name and trigger Path A's
  // replace branch on the unrelated paragraph that follows. Real
  // headings rarely end with .?! — trailing colons get stripped by
  // `normalise` upstream so they don't reach this check.
  if (/[.?!]\s*$/.test(text)) return false;
  return true;
};

/**
 * For each module with a non-empty value, find a paragraph in the doc
 * whose text matches the module name (normalised) and replace the inline
 * content of the very next paragraph with the module's text. The next-
 * paragraph node is preserved (so its style — alignment, spacing — stays
 * intact); only its inline content is swapped.
 *
 * Multiple matching headings? We only act on the first match per module
 * — chosen-choice content is single-shot per section.
 *
 * No matching heading? Module is skipped silently. (No insertion at
 * end-of-doc — that's how we got into trouble with the slot-rebuild.)
 *
 * `hints` is the backend-computed outline for the active template. When
 * supplied, Path A only treats a paragraph as a heading if it's in
 * `hints.knownHeadings` — that's what fixes MDRS-97 (tab-aligned body
 * rows no longer fool the substring matcher).
 *
 * @returns count of modules whose content was applied.
 */
export const applyModuleContent = (
  view: EditorView,
  modules: ModuleInsert[],
  hints?: OutlineHints,
  /** MDRS-227: names of modules already anchor-inserted (Path B) this editor
   *  session. Mutated in place. When a module is present here, the anchor-insert
   *  fallback is skipped so a re-fire of the debounced insertion effect can't
   *  add a duplicate heading if the in-doc exists-check fails to recognise the
   *  heading a previous fire inserted. Path A (attach to an existing heading) is
   *  unaffected — this only gates the synthesise-a-new-heading path. */
  anchorInserted?: Set<string>
): number => {
  const candidates = modules.filter(
    (m) => (m.value && m.value.trim().length > 0) || m.alwaysInsert
  );
  if (candidates.length === 0) return 0;

  const schema = view.state.schema;
  const paragraphType = schema.nodes.paragraph ?? schema.nodes.doc_paragraph;
  const strongMark = schema.marks.strong ?? schema.marks.bold;
  if (!paragraphType) return 0;

  // Walk paragraphs once, collecting positions + the node itself so the
  // anchor-insert path can clone paragraph attrs and first-text-run marks
  // (so the inserted heading inherits the surrounding doc's font/style).
  type Para = {
    node: PMNode;
    text: string;
    from: number;
    to: number;
    size: number;
  };
  const paragraphs: Para[] = [];
  view.state.doc.descendants((node, pos) => {
    if (node.type.name === "paragraph" || node.type.name === "doc_paragraph") {
      paragraphs.push({
        node,
        text: node.textContent,
        from: pos,
        to: pos + node.nodeSize,
        size: node.nodeSize,
      });
      // Don't descend into a paragraph's children — they're inline.
      return false;
    }
    return true;
  });

  // Plan all mutations in pre-mutation coordinates: either a `replace`
  // (heading found → swap next paragraph's inline content) or an `insert`
  // (heading missing → emit a fresh heading + content block at an anchor).
  type Replacement =
    | { kind: "replace"; from: number; to: number; content: Fragment }
    | { kind: "insert"; at: number; nodes: PMNode[] };
  const replacements: Replacement[] = [];
  const claimedNext = new Set<number>(); // paragraph indices already used

  const buildInlineNodes = (value: string, marks: readonly Mark[] = []) =>
    buildInlineNodesShared(schema, value, marks);

  for (const m of candidates) {
    const hasContent = !!m.value && m.value.trim().length > 0;
    const matchTargets = headingMatchTargets(m);

    // 1. Existing-heading path — find a matching heading and refresh the
    //    next *non-blank* paragraph's content. Skip when value is empty
    //    (don't blank out an existing paragraph just because the user
    //    hasn't picked a jurisdiction yet).
    //
    //    Skipping blanks matters for our anchor-insert layout, which lays
    //    down [heading][spacer][content][spacer]: the literal next
    //    paragraph after the heading is the spacer, not the content.
    let matched = false;
    if (hasContent) {
      for (let i = 0; i < paragraphs.length - 1; i++) {
        if (!isHeadingCandidate(paragraphs[i].text, hints)) continue;
        if (!matchTargets.some((t) => headingMatches(paragraphs[i].text, t)))
          continue;
        // Find the next paragraph that actually has content. Bail past
        // blanks (max 3 to avoid drifting into the following heading).
        let target = -1;
        for (let j = i + 1; j <= Math.min(i + 4, paragraphs.length - 1); j++) {
          if (claimedNext.has(j)) continue;
          if (paragraphs[j].text.trim().length === 0) continue;
          target = j;
          break;
        }
        // preserveExisting (CoC jurisdiction fallback): never overwrite the
        // template's own content. If the section already has non-blank body
        // text (target found above), leave it exactly as authored and consider
        // the module satisfied — only an empty section (target === -1 below)
        // gets the fallback declaration. QA: a template's own Code of Conduct
        // was being replaced by the generic jurisdiction default.
        if (m.preserveExisting && target !== -1) {
          matched = true;
          break;
        }
        if (target === -1) {
          // All subsequent slots are blank or claimed — fall back to i+1.
          if (claimedNext.has(i + 1)) continue;
          target = i + 1;
        }
        const next = paragraphs[target];
        // Clone the target paragraph's first-run marks (font, size, color,
        // weight) onto the new text — otherwise we strip whatever marks the
        // template's body run had and the refreshed content renders in
        // schema defaults instead of the template's body font. QA-reported
        // visual mismatch under MDRS-60.
        const targetBodyMarks = firstRunMarks(next.node);
        replacements.push({
          kind: "replace",
          from: next.from + 1,
          to: next.to - 1,
          content: Fragment.from(buildInlineNodes(m.value, targetBodyMarks)),
        });
        claimedNext.add(target);
        matched = true;
        break;
      }
    } else {
      // Empty value but alwaysInsert: find any matching existing heading
      // so we don't add a duplicate. Same hints gate as Path A — a
      // tab-aligned body row containing the module name must not be
      // treated as an existing heading (MDRS-97).
      let existingIdx = -1;
      for (let i = 0; i < paragraphs.length; i++) {
        if (!isHeadingCandidate(paragraphs[i].text, hints)) continue;
        if (!matchTargets.some((t) => headingMatches(paragraphs[i].text, t)))
          continue;
        existingIdx = i;
        break;
      }
      matched = existingIdx !== -1;

      // Backfill the leading spacer for synthesised preamble headings
      // when the heading sits flush against a non-blank paragraph above.
      // Covers drafts saved before the anchor-insert path started
      // emitting a lead spacer. Idempotent — once the spacer exists,
      // the previous paragraph IS blank on the next pass so this
      // branch is a no-op. We deliberately don't gate on whether `prev`
      // is a heading: `isHeadingCandidate` is a permissive heuristic
      // (any short non-tabbed text qualifies without hints), and even
      // when prev IS a real heading, inserting a blank between two
      // adjacent headings is a visual win, not a regression.
      if (matched && m.alwaysInsert && existingIdx > 0) {
        const prev = paragraphs[existingIdx - 1];
        if (prev.text.trim().length > 0) {
          const leadBodyAttrs = visualParaAttrs(prev.node);
          replacements.push({
            kind: "insert",
            at: paragraphs[existingIdx].from,
            nodes: [paragraphType.create(leadBodyAttrs, Fragment.empty)],
          });
        }
      }
    }
    if (matched) continue;

    // 2. Anchor-fallback path — heading absent, try to insert before/after a
    //    known anchor in the doc.
    if (!m.fallbackAnchors || m.fallbackAnchors.length === 0) continue;

    // MDRS-227: only ever synthesise this module's heading ONCE per session.
    // The insertion effect re-fires on every async input change and the
    // exists-check above is the only idempotency guard — if it fails to
    // recognise the heading a previous fire inserted (unreliable on templates
    // the outline classifier misses), Path B re-inserts a duplicate on each
    // re-fire. This bounds it to a single insert without touching the
    // heading-recognition heuristic (avoiding the MDRS-97 seesaw); it can only
    // prevent a re-insert, never suppress the first one.
    if (anchorInserted?.has(m.name)) continue;

    let anchorPos: number | null = null;
    let anchorIdx = -1;
    let anchorPosition: "before" | "after" = "before";
    for (const anchor of m.fallbackAnchors) {
      const idx = paragraphs.findIndex((p) => anchorMatches(p.text, anchor.match));
      if (idx === -1) continue;
      anchorIdx = idx;
      anchorPosition = anchor.position;
      anchorPos =
        anchor.position === "before" ? paragraphs[idx].from : paragraphs[idx].to;
      break;
    }
    if (anchorPos === null || anchorIdx === -1) continue;

    // Pick the donor paragraphs for attrs + run marks. The goal is that
    // CODE OF CONDUCT inherits SERVICE STANDARDS-style chrome and the body
    // paragraph below it inherits the body style — so the inserted block
    // looks like the existing template, not a default-styled stub.
    //   • "before SERVICE STANDARDS" → heading donor = SERVICE STANDARDS
    //     (the anchor), body donor = paragraph after it.
    //   • "after Thank you…" (fallback) → no clear heading donor in scope,
    //     so we look at the paragraph after the anchor for a heading shape
    //     (template typically has a heading next); body donor = anchor.
    let headingDonor: PMNode | null;
    let bodyDonor: PMNode | null;
    if (anchorPosition === "before") {
      headingDonor = paragraphs[anchorIdx].node;
      bodyDonor = paragraphs[anchorIdx + 1]?.node ?? null;
    } else {
      const after = paragraphs[anchorIdx + 1]?.node ?? null;
      headingDonor = after ?? paragraphs[anchorIdx].node;
      bodyDonor = paragraphs[anchorIdx].node;
    }

    const headingAttrs = visualParaAttrs(headingDonor);
    const bodyAttrs = visualParaAttrs(bodyDonor);
    const baseHeadingMarks = headingDonor ? firstRunMarks(headingDonor) : [];
    const headingRunMarks = baseHeadingMarks.length
      ? baseHeadingMarks
      : strongMark
        ? [strongMark.create()]
        : [];
    const bodyRunMarks = bodyDonor ? firstRunMarks(bodyDonor) : [];

    const headingText = m.headingLabel ?? m.name;
    const headingNode = paragraphType.create(
      headingAttrs,
      headingText
        ? Fragment.from(schema.text(headingText, headingRunMarks))
        : Fragment.empty
    );
    const contentNode = paragraphType.create(
      bodyAttrs,
      hasContent
        ? Fragment.from(buildInlineNodes(m.value, bodyRunMarks))
        : Fragment.empty
    );
    // Empty spacer paragraphs above the heading and around the content:
    // a leading one so the heading doesn't butt up against the previous
    // body paragraph (e.g. "…provided by <<Client_SalLastName>>." running
    // straight into CODE OF CONDUCT), one between heading and body for
    // visual breathing room, and one after the body so the next template
    // heading isn't squashed against our content. All use bodyAttrs so
    // they inherit the body paragraph's spacing rather than the heading's.
    const leadSpacerNode = paragraphType.create(bodyAttrs, Fragment.empty);
    const headSpacerNode = paragraphType.create(bodyAttrs, Fragment.empty);
    const tailSpacerNode = paragraphType.create(bodyAttrs, Fragment.empty);
    replacements.push({
      kind: "insert",
      at: anchorPos,
      nodes: [
        leadSpacerNode,
        headingNode,
        headSpacerNode,
        contentNode,
        tailSpacerNode,
      ],
    });
    // Record that we synthesised this module's heading so a later re-fire
    // won't add a second one (MDRS-227).
    anchorInserted?.add(m.name);
  }

  if (replacements.length === 0) {
    // Diagnostic: log once per call so we can see in DevTools whether
    // the helper ran but found no heading matches (vs. didn't run at
    // all). Remove once heading matching is verified across templates.
    // eslint-disable-next-line no-console
    console.debug("[module-insert] no matches; modules vs paragraphs:", {
      modules: candidates.map((m) => ({ name: m.name, norm: normalise(m.name) })),
      paragraphs: paragraphs.map((p) => ({
        text: p.text.slice(0, 80),
        norm: normalise(p.text).slice(0, 80),
      })),
    });
    return 0;
  }

  // Apply right-to-left so earlier offsets remain valid. `replace`s use
  // [from, to]; `insert`s use [at, at]. Sort by `from`/`at` descending,
  // and break ties by reversed push order so that two inserts at the same
  // anchor (the 4-preamble case) land in input order in the final doc —
  // each `tr.insert(P, X)` prepends X before whatever's already at P, so
  // applying the LAST input first leaves the FIRST input on top.
  const posOf = (r: Replacement) => (r.kind === "replace" ? r.from : r.at);
  const indexed = replacements.map((r, idx) => ({ r, idx }));
  indexed.sort((a, b) => posOf(b.r) - posOf(a.r) || b.idx - a.idx);
  let tr = view.state.tr;
  for (const { r } of indexed) {
    if (r.kind === "replace") {
      tr = tr.replaceWith(r.from, r.to, r.content);
    } else {
      tr = tr.insert(r.at, Fragment.from(r.nodes));
    }
  }
  view.dispatch(tr);
  return replacements.length;
};
